<template>
  <div ref="scheduleDropdownRef" :class="$style.scheduleDropdown">
    <BaseButton
      ref="scheduleButtonRef"
      v-bind="attrs"
      :presentation="props.presentation"
      :size="props.size"
      aria-haspopup="menu"
      :aria-expanded="isOpen"
      @click.stop="toggleDropdown"
    >
      <IconCheckmark
        v-if="props.scheduledAllStores"
        :class="$style.checkmark"
      />
      {{ label }}
      <IconChevronDown v-if="!isOpen" :class="$style.chevron" />
      <IconChevronUp v-if="isOpen" :class="$style.chevron" />
    </BaseButton>

    <div v-if="isOpen" :class="$style.scheduleDropdownOptionsWrap">
      <ul :class="$style.scheduleDropdownOptions" role="menu">
        <li
          v-for="(store, index) in stores"
          :key="store?.id"
          :class="$style.scheduleDropdownItem"
        >
          <button
            :ref="(element) => setItemRef(element, index)"
            type="button"
            :class="$style.scheduleDropdownItemButton"
            role="menuitem"
            @click.prevent="selectStore(store)"
            @keydown="handleItemKeydown($event, index, store)"
          >
            {{ store?.name }}
            <IconCheckmark
              v-if="scheduledStore(store)"
              :class="$style.checkmark"
            />
          </button>
        </li>
      </ul>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { ITabletopOrganization } from '~/types/tabletop'
import { IconCheckmark, IconChevronDown, IconChevronUp } from '#components'

defineOptions({
  inheritAttrs: false,
})

// Shared state for schedule dropdown - determines which dropdown is currently open
const openDropdownId = useState<string>('schedule-dropdown-open', () => '')

interface ScheduleDropdownProps {
  scheduledAllStores?: boolean
  href?: string
  label?: string
  parentEntryTitle?: string
  id?: string
  scheduleId?: string
  presentation?:
    | 'default'
    | 'dark'
    | 'light'
    | 'invisible'
    | 'strip'
    | 'two-tones'
    | 'clear'
    | 'outline'
    | 'translucent'
    | 'orange'
  target?: '_blank' | '_self' | '_parent' | '_top'
  size?: 'default' | 'large' | 'small'
}

const props = withDefaults(defineProps<ScheduleDropdownProps>(), {
  label: 'Schedule Events',
  href: '',
  parentEntryTitle: '',
  id: () => crypto.randomUUID(),
  scheduleId: '',
  presentation: 'orange',
  target: '_self',
  size: 'default',
  scheduledAllStores: false,
})

const userStore = useUserStore()
const attrs = useAttrs()

const { stores } = storeToRefs(userStore)

const isOpen = computed(() => openDropdownId.value === props.id)

const scheduleButtonRef =
  useTemplateRef<ComponentPublicInstance>('scheduleButtonRef')
const itemRefs = ref<HTMLElement[]>([])

const setItemRef = (element: unknown, index: number) => {
  if (element instanceof HTMLElement) itemRefs.value[index] = element
}

const focusItemAtIndex = (index: number) => {
  itemRefs.value[index]?.focus()
}

const appendStoreId = (
  url: string | undefined,
  storeId: string | undefined,
) => {
  if (!url || !storeId) return url
  const separator = url.includes('?') ? '&' : '?'
  return `${url}${separator}store=${storeId}`
}

const scheduleId = computed(() => {
  if (props.scheduleId) return props.scheduleId

  // Note: without below, client have to fill all created events in contentful with scheduleId.
  if (!props.href) return ''

  try {
    // Note: Not all href has ?template=whatever, so we need to safely check for it
    const url = new URL(props.href)
    if (
      !['http:', 'https:'].includes(url.protocol) ||
      url.hostname !== 'eventlink.wizards.com'
    ) {
      return ''
    }

    return url.searchParams.get('template') || ''
  } catch {
    return ''
  }
})

const scheduledStore = (
  store: ITabletopOrganization | Record<string, unknown> | null | undefined,
) => {
  if (!scheduleId.value) return false

  const checkedStore = stores.value.find((s) => s?.id === store?.id)

  return !!checkedStore?.templateUsages?.find(
    (t) => t?.templateId === scheduleId.value && (t?.count ?? 0) > 0,
  )
}

const toggleDropdown = () => {
  openDropdownId.value = isOpen.value ? '' : props.id
}

const scheduleDropdownRef = ref<HTMLElement | null>(null)

const handleClickOutside = (event: MouseEvent) => {
  if (
    scheduleDropdownRef.value &&
    !scheduleDropdownRef.value.contains(event.target as Node)
  ) {
    openDropdownId.value = ''
  }
}

const closeDropdown = (restoreFocus = false) => {
  openDropdownId.value = ''

  if (restoreFocus) {
    nextTick(() => {
      ;(
        (scheduleButtonRef.value?.$el as HTMLElement | undefined) ?? null
      )?.focus()
    })
  }
}

// Note: match with VueSelect keyboard handling for item navigation
const handleItemKeydown = (
  event: KeyboardEvent,
  index: number,
  store: ITabletopOrganization | Record<string, unknown> | null | undefined,
) => {
  switch (event.key) {
    case 'ArrowDown':
      event.preventDefault()
      focusItemAtIndex((index + 1) % itemRefs.value.length)
      break
    case 'ArrowUp':
      event.preventDefault()
      focusItemAtIndex(
        (index - 1 + itemRefs.value.length) % itemRefs.value.length,
      )
      break
    case 'Enter':
      event.preventDefault()
      selectStore(store)
      break
    case 'Escape':
      event.preventDefault()
      closeDropdown(true)
      break
    case 'Tab':
      closeDropdown()
      break
    default:
      break
  }
}

watch(isOpen, async (open) => {
  if (!open) {
    itemRefs.value = []
    return
  }

  await nextTick()
  focusItemAtIndex(0)
})

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
})

onBeforeUnmount(() => {
  document.removeEventListener('click', handleClickOutside)
})

const selectStore = (
  store: ITabletopOrganization | Record<string, unknown> | null | undefined,
) => {
  if (!store || !props.href) return

  const finalUrl = appendStoreId(props.href, store?.id as string)

  // Track GA event
  pushToGaDataLayer({
    event: 'ga4.trackEvent',
    eventName: 'schedule_click',
    eventParams: {
      link_text: props.label,
      link_url: finalUrl,
      entry_title: 'WPN-EVENT-CTA',
      parent_entry_title: props.parentEntryTitle?.trim() ?? null,
    },
  })

  // Open link
  window.open(finalUrl, props.target)

  // Close dropdown
  closeDropdown()
}
</script>

<style lang="scss" module>
.scheduleDropdown {
  position: relative;
  display: inline-flex;
  width: auto;
  max-width: 100%;
  flex: 0 0 auto;

  @include media('sm') {
    min-width: 200px;
  }
}

.scheduleDropdownOptionsWrap {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  z-index: 1000;
  border: 1px solid var(--gray-5);
  border-radius: var(--border-radius);
  background: var(--white);
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.scheduleDropdownOptions {
  list-style: none;
  margin: 0;
  padding: 0;
}

.scheduleDropdownItem {
  margin: 0;
}

.scheduleDropdownItemButton {
  all: unset;
  box-sizing: border-box;
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: 10px 14px;
  font-weight: 600;
  font-size: 16px;
  text-align: left;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover,
  &:focus-visible {
    background: var(--gray-1);
    color: var(--purple);
  }
}

.checkmark {
  display: inline-block;
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  color: var(--green);
  margin-right: 10px;
}

.chevron {
  display: block;
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  align-self: center;
  margin-left: 10px;
}
</style>
